import { ScheduledAction } from '@demind-inc/core';
import dayjs from 'dayjs';

import { AssistantChatMessage, ChatSuggestedEvent, ChatSuggestedEventAction } from '../types';

/** Matches OpenAPI `PendingItem`. Kept unexported so the data-access barrel
 * does not clash with the generated type of the same name. */
interface PendingItem {
  action_id: string;
  name: string;
  start_time?: string;
  end_time?: string;
  event_from?: string;
  category?: string;
}

const MAX_PENDING_ITEMS = 30;

/** Same prefix as chat.helpers TEMPORAL_EVENT_PREFIX — keep local to avoid RN barrel imports in unit tests. */
const TEMPORAL_EVENT_PREFIX = 'temporal-';

const SYNTHETIC_ACTION_ID_PREFIXES = [
  TEMPORAL_EVENT_PREFIX,
  'plan-',
  'result-json-',
  'ai_scheduler_',
] as const;

/** A brand-new task/routine suggestion (no `todo_id`/`routineActivityId` yet —
 * the backend only assigns one on commit, unlike calendar events which get a
 * real `action_id` immediately) falls back to a `temporal-task-`/
 * `temporal-routine-` id derived from its name (see resultJsonTaskAdjustments
 * .helpers/resultJsonRoutineAdjustments.helpers). That id is deterministic
 * (stable across re-renders for the same name), and science's PENDING_ITEMS
 * matching is name-based, not action_id-based — so unlike the other synthetic
 * prefixes above (index-based `plan-`/`result-json-`, or meal/sleep temporal
 * previews), it's safe to advertise rather than silently drop.  */
const ALLOWED_SYNTHETIC_ACTION_ID_PREFIXES = ['temporal-task-', 'temporal-routine-'] as const;

/** Mirrors allPlanCache.helpers getAllPlanScheduleId without importing that chain. */
const getScheduleId = (schedule: ScheduledAction, fallbackIndex = 0): string =>
  String(schedule.id ?? `plan-${fallbackIndex}`);

const isUsableActionId = (actionId: string | undefined | null): actionId is string => {
  if (!actionId || typeof actionId !== 'string') {
    return false;
  }
  const trimmed = actionId.trim();
  if (!trimmed) {
    return false;
  }
  if (ALLOWED_SYNTHETIC_ACTION_ID_PREFIXES.some((prefix) => trimmed.startsWith(prefix))) {
    return true;
  }
  return !SYNTHETIC_ACTION_ID_PREFIXES.some((prefix) => trimmed.startsWith(prefix));
};

const toHHmm = (isoOrDate: string | undefined | null): string | undefined => {
  if (!isoOrDate) {
    return undefined;
  }
  const parsed = dayjs(isoOrDate);
  return parsed.isValid() ? parsed.format('HH:mm') : undefined;
};

const pushPendingItem = (
  byActionId: Map<string, PendingItem>,
  item: {
    actionId?: string | null;
    name?: string | null;
    startTime?: string | null;
    endTime?: string | null;
    eventFrom?: string | null;
    category?: string | null;
  }
) => {
  const actionId = item.actionId?.trim();
  const name = item.name?.trim();
  if (!isUsableActionId(actionId) || !name) {
    return;
  }

  // Last write wins: callers add sources in chronological order (AllPlan,
  // then temporal previews, then chat messages oldest-to-newest), so a later
  // entry for the same action_id reflects a reschedule and should replace
  // the earlier one rather than be dropped.
  const pending: PendingItem = {
    action_id: actionId,
    name,
  };

  const startTime = toHHmm(item.startTime);
  if (startTime) {
    pending.start_time = startTime;
  }
  const endTime = toHHmm(item.endTime);
  if (endTime) {
    pending.end_time = endTime;
  }
  if (item.eventFrom) {
    pending.event_from = item.eventFrom;
  }
  if (item.category) {
    pending.category = item.category;
  }

  byActionId.set(actionId, pending);
};

const collectFromScheduledActions = (
  byActionId: Map<string, PendingItem>,
  schedules: ScheduledAction[],
  options?: { acceptedIds?: string[]; discardedIds?: string[] }
) => {
  const accepted = new Set(options?.acceptedIds ?? []);
  const discarded = new Set(options?.discardedIds ?? []);

  schedules.forEach((schedule, index) => {
    const scheduleId = getScheduleId(schedule, index);
    if (accepted.has(scheduleId) || discarded.has(scheduleId)) {
      return;
    }
    pushPendingItem(byActionId, {
      actionId: schedule.id,
      name: schedule.name,
      startTime: schedule.startDate,
      endTime: schedule.endDate,
      eventFrom: schedule.eventFrom,
      category: schedule.categoryId,
    });
  });
};

/** Suggestion actions worth advertising as still-pending: live cards for a
 * calendar event, task, or routine. Delete/remove variants and
 * checkOnCalendar are excluded — nothing to revise-in-place there. */
const INCLUDED_PENDING_ACTIONS = new Set<ChatSuggestedEventAction>([
  'setEvent',
  'setTask',
  'setRoutine',
]);

/** Delete/remove variants don't get a revise-in-place pending card (see
 * INCLUDED_PENDING_ACTIONS), but a remove for an action_id already staged by
 * an earlier source (AllPlan schedules, an earlier chat turn) must actively
 * clear that stale entry rather than just skip adding a new one. */
const REMOVE_ACTIONS = new Set<ChatSuggestedEventAction>([
  'deleteEvent',
  'deleteRoutine',
  'deleteTask',
]);

const collectFromSuggestedEvents = (
  byActionId: Map<string, PendingItem>,
  events: ChatSuggestedEvent[]
) => {
  events.forEach((event) => {
    const actionId = (event.autoScheduleFrom?.id || event.eventId)?.trim();
    if (event.action && REMOVE_ACTIONS.has(event.action)) {
      if (actionId) {
        byActionId.delete(actionId);
      }
      return;
    }
    if (event.action && !INCLUDED_PENDING_ACTIONS.has(event.action)) {
      return;
    }
    pushPendingItem(byActionId, {
      actionId,
      name: event.name,
      startTime: event.startTime,
      endTime: event.endTime,
      eventFrom: event.autoScheduleFrom?.from,
      category: event.categoryId,
    });
  });
};

/**
 * Build science `pending_items` for a v4 chat turn from uncommitted cards still
 * on screen (Plan My Day, freeText temporal previews, chat suggested blocks —
 * events, tasks, and routines alike — AI Scheduler). Requires `action_id` +
 * `name` so reschedules reuse the same card.
 */
export const collectPendingItemsForChat = ({
  allPlanSchedules = [],
  allPlanAcceptedIds = [],
  allPlanDiscardedIds = [],
  temporalSuggestedEvents = [],
  messages = [],
  aiScheduledActions = [],
}: {
  allPlanSchedules?: ScheduledAction[];
  allPlanAcceptedIds?: string[];
  allPlanDiscardedIds?: string[];
  temporalSuggestedEvents?: ChatSuggestedEvent[];
  messages?: AssistantChatMessage[];
  aiScheduledActions?: ScheduledAction[];
} = {}): PendingItem[] => {
  const byActionId = new Map<string, PendingItem>();

  collectFromScheduledActions(byActionId, allPlanSchedules, {
    acceptedIds: allPlanAcceptedIds,
    discardedIds: allPlanDiscardedIds,
  });

  collectFromSuggestedEvents(byActionId, temporalSuggestedEvents);

  messages.forEach((message) => {
    if (message.suggestedBlocks?.length) {
      collectFromSuggestedEvents(byActionId, message.suggestedBlocks);
    }
  });

  collectFromScheduledActions(byActionId, aiScheduledActions, {
    acceptedIds: allPlanAcceptedIds,
    discardedIds: allPlanDiscardedIds,
  });

  return Array.from(byActionId.values()).slice(0, MAX_PENDING_ITEMS);
};
